import { useEffect, useMemo, useRef } from 'react';

/**
 * The records in `added` whose id is not already in `list`. Callers merge with
 * this inside their state updater: the section's own load can land in the same
 * render as a merge, and appending blindly would show a tile twice.
 */
export const notYetIn = (list, added) => {
  const ids = new Set((list || []).map((r) => r?.id));
  return (added || []).filter((r) => !ids.has(r?.id));
};

/**
 * Keep a sidebar record section (Phenotypes, Genotypes, Diseases) in step when
 * its person's row in `memberRecords` gains a record the section is not showing.
 *
 * Each of those sections keeps its OWN list, loaded when the person is selected
 * and changed only by the section's own add / edit / delete. A record added to
 * the person from anywhere else — a disease, phenotype or genotype dragged from
 * the legend onto their symbol — lands in `memberRecords` and on the chart's
 * labels, but the open section went on saying "none recorded" until another
 * person was selected and this one re-selected.
 *
 * The row already holds the server's list for this person (the write's
 * response), so the section MERGES the missing records in rather than
 * refetching: that is what its own add does with the same response, it costs no
 * request, and it cannot clear anything the user has selected but not saved.
 *
 * Only a CHANGE of the row's ids for the SAME person triggers it: on first
 * render and on a switch to another person the section's own effect is already
 * loading. Records the section already shows are never passed on, so its own
 * add — which updates its list and the row together — is a no-op here.
 *
 * @param {object} opts
 * @param {object[]} opts.memberRecords
 * @param {*} opts.pid  the person the section is showing
 * @param {string} opts.field  'phenotypes' | 'genotypes' | 'diagnoses'
 * @param {(record: object) => boolean} [opts.keep]  which of the row's records
 *   this section lists (a module-level function, so it is stable)
 * @param {string[]} opts.shownIds  ids the section currently lists
 * @param {(added: object[]) => void} opts.onGained  merge these in
 */
export const useRowRecordSync = ({
  memberRecords,
  pid,
  field,
  keep,
  shownIds,
  onGained
}) => {
  const rowRecords = useMemo(() => {
    const row = (memberRecords || []).find((m) => m.pid === pid);
    return (row?.[field] || []).filter((r) => r?.id && (!keep || keep(r)));
  }, [memberRecords, pid, field, keep]);

  const rowKey = useMemo(
    () =>
      rowRecords
        .map((r) => r.id)
        .sort()
        .join('\n'),
    [rowRecords]
  );

  const previous = useRef({ pid, rowKey });
  const latest = useRef({});
  latest.current = { shownIds, onGained, rowRecords };

  useEffect(() => {
    const before = previous.current;
    previous.current = { pid, rowKey };
    if (before.pid !== pid || before.rowKey === rowKey || !rowKey) return;

    const shown = new Set(latest.current.shownIds || []);
    const added = latest.current.rowRecords.filter((r) => !shown.has(r.id));
    if (added.length > 0) latest.current.onGained(added);
  }, [pid, rowKey]);
};
